/**
 * Where a right-click menu goes.
 *
 * A `Pop` panel hangs off a trigger's box, so it can be right-aligned to it and
 * flipped above it. A context menu has no trigger - it has a pointer, which can
 * be one pixel from the window's edge - so the rules are different enough to be
 * their own function: grow down and to the right of the pointer, flip to the
 * other side when that side has no room, and only then clamp.
 *
 * Kept free of React and the DOM so the arithmetic can be tested. `Pop.tsx` does
 * its own measuring inline; this one is separate because the flip has four cases
 * and each of them is a place a menu can end up half off the screen.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Placed {
  top: number;
  left: number;
  /** Opened above the pointer, which the entry animation has to match. */
  up: boolean;
}

/** The least the panel keeps from the window edge. Same value as `Pop`. */
const EDGE = 10;

/**
 * The panel's corner sits a hair off the pointer rather than under it, so the
 * cursor is never already hovering the first row when the menu appears - which
 * reads as a row having been picked before anything was chosen.
 */
const NUDGE = 2;

/**
 * Place the panel for a pointer at `point`.
 *
 * The flip is conditional on both sides, not on the first one that fails: a
 * panel taller than the window has no room either way, and flipping it then
 * would move it up without making it fit, so it stays down and the clamp deals
 * with it (the stylesheet gives `.crm-menu` its own `max-height` and scrolls).
 */
export function placeAtPoint(point: Point, panel: Size, viewport: Size): Placed {
  const roomBelow = viewport.height - EDGE - (point.y + NUDGE);
  const roomAbove = point.y - NUDGE - EDGE;
  const up = panel.height > roomBelow && panel.height <= roomAbove;

  const roomRight = viewport.width - EDGE - (point.x + NUDGE);
  const roomLeft = point.x - NUDGE - EDGE;
  const leftwards = panel.width > roomRight && panel.width <= roomLeft;

  const wantedTop = up ? point.y - NUDGE - panel.height : point.y + NUDGE;
  const wantedLeft = leftwards ? point.x - NUDGE - panel.width : point.x + NUDGE;

  return {
    up,
    // `Math.max(EDGE, ...)` last on both axes: a panel bigger than the window
    // has to start at the edge and scroll, not sit at a negative offset where
    // its first rows are unreachable.
    top: Math.max(EDGE, Math.min(wantedTop, viewport.height - EDGE - panel.height)),
    left: Math.max(EDGE, Math.min(wantedLeft, viewport.width - EDGE - panel.width)),
  };
}
